import React from 'react';
import { Table, Badge, Spinner, RowActions, ACTIONS_COL_WIDTH } from '@/components/ui';
import { QuantifiedPlan } from '../../../services/areasService';
import { formatDollars } from '../../../utils/formatDollars';
import { formatDate as formatPacificDate } from '@/utils/displayDateTime';

interface QuantifiedPlansTableProps {
  quantifiedPlans: QuantifiedPlan[];
  onEdit: (plan: QuantifiedPlan) => void;
  onDelete: (id: number) => void;
  loading?: boolean;
}

const QuantifiedPlansTable: React.FC<QuantifiedPlansTableProps> = ({
  quantifiedPlans,
  onEdit,
  onDelete,
  loading = false
}) => {
  const formatDate = (date: string | Date | undefined): string => {
    return formatPacificDate(date) || '–';
  };

  const formatAdjustment = (value: number): string => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${formatDollars(value)}`;
  };

  if (loading) {
    return (
      <div className="tw-text-center tw-py-4">
        <Spinner animation="border" variant="primary" role="status">
          <span className="tw-sr-only">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (quantifiedPlans.length === 0) {
    return (
      <div className="tw-text-center tw-py-4">
        <p className="tw-text-content-muted">No quantified plans found. Click "Add Quantified Plan" to create one.</p>
      </div>
    );
  }

  return (
    <div className="tw-overflow-x-auto">
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Status</th>
            <th>Active</th>
            <th>Contract Year</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Scheduled 2 Contract Amount</th>
            <th>Annual Adjustment</th>
            <th>Target</th>
            <th style={{ width: ACTIONS_COL_WIDTH }} className="tw-text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {quantifiedPlans.map((plan) => {
            const target = plan.annualTarget + plan.annualAdjustment;
            return (
              <tr key={plan.id}>
                <td>
                  <Badge bg={plan.status === 'Open' ? 'success' : 'secondary'}>
                    {plan.status}
                  </Badge>
                </td>
                <td>
                  {plan.isActive ? (
                    <Badge bg="primary">Active</Badge>
                  ) : (
                    <span className="tw-text-content-muted">–</span>
                  )}
                </td>
                <td>{plan.year}</td>
                <td>{formatDate(plan.startDate)}</td>
                <td>{formatDate(plan.endDate)}</td>
                <td>{formatDollars(plan.annualTarget)}</td>
                <td className={plan.annualAdjustment >= 0 ? 'tw-text-success' : 'tw-text-danger'}>
                  {formatAdjustment(plan.annualAdjustment)}
                </td>
                <td><strong>{formatDollars(target)}</strong></td>
                <td>
                  <RowActions
                    actions={[
                      { kind: 'edit', label: 'Edit Quantified Plan', onClick: () => onEdit(plan) },
                      { kind: 'delete', label: 'Delete Quantified Plan', onClick: () => onDelete(plan.id) },
                    ]}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default QuantifiedPlansTable;

